import React from 'react'
import { ScrollRestoration, useLocation } from 'react-router-dom'

const DEFAULT_RESTORE_KEY = 'default'

/**
 * Get type of last navigation
 * https://caniuse.com/mdn-api_performance_getentriesbytype
 */
function getIsInitiallyRendered() {
  const navTypes = window.performance
    ?.getEntriesByType?.('navigation')
    .map((nav) => (nav as PerformanceNavigationTiming).type)

  return navTypes?.includes('reload') || navTypes?.includes('back_forward')
}

/**
 * Some of our views are based on query params (unsupported by router),
 * which creates problems with scroll restore navigating between pages (eg. quote)
 *
 * In order to work around this, we need to only restore the scroll on page reload
 * and not on navigation. But at the same time, we need to write the scroll position,
 * so we can restore it if we need to.
 *
 * We can’t simply render the component conditionally, because it’d restore scroll
 * on mount, so we need a way of restoring scroll only on reload, while preserving
 * the saving functionality, which we do by switching the key in an effect after the component has rendered
 */
export function ScrollRestore() {
  const loc = useLocation()
  const [resetKey, setKey] = React.useState(() =>
    getIsInitiallyRendered()
      ? DEFAULT_RESTORE_KEY
      : loc.key === DEFAULT_RESTORE_KEY
        ? Math.random().toString(16)
        : loc.key
  )

  React.useEffect(() => setKey(() => DEFAULT_RESTORE_KEY), [])

  return <ScrollRestoration getKey={() => resetKey} />
}
